import { useState, useRef, useEffect, useCallback } from 'react';
import { get } from '../../../../api/client';
import type { OrderItem } from '../../../../types';
import OrderExportPreview from './OrderExportPreview';
import type { ExportConfig, ExportTemplate } from './exportTypes';
import { DEFAULT_EXPORT_CONFIG, TEMPLATE_LABELS } from './exportTypes';

interface OrderItemDetail {
  id: string;
  itemName: string | null;
  itemSpec: string | null;
  quantity: number | null;
  unitPrice: number | null;
}

interface Props {
  order: OrderItem;
  onClose: () => void;
}

const TEMPLATE_COLORS: Record<ExportTemplate, { ring: string; dot: string; label: string }> = {
  minimal:  { ring: 'ring-teal-400',   dot: 'bg-teal-500',   label: 'bg-white border border-gray-200' },
  business: { ring: 'ring-slate-500',  dot: 'bg-slate-700',  label: 'bg-slate-800 text-white' },
  warm:     { ring: 'ring-amber-400',  dot: 'bg-amber-500',  label: 'bg-amber-50 border border-amber-200' },
};

function SectionToggle({
  label, checked, onChange,
}: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="flex items-center justify-between py-2 cursor-pointer group">
      <span className="text-sm text-gray-700 group-hover:text-gray-900 transition-colors">{label}</span>
      <button
        type="button"
        onClick={() => onChange(!checked)}
        className={`relative w-9 h-5 rounded-full transition-colors ${checked ? 'bg-teal-500' : 'bg-gray-200'} cursor-pointer`}
      >
        <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform ${checked ? 'translate-x-4' : ''}`} />
      </button>
    </label>
  );
}

export default function OrderExportModal({ order, onClose }: Props) {
  const [config, setConfig] = useState<ExportConfig>(DEFAULT_EXPORT_CONFIG);
  const [orderItems, setOrderItems] = useState<OrderItemDetail[]>([]);
  // 明细读取失败必须说出来。让它落回空数组，卡片上就变成「这张单没有明细」，
  // 而纸面合计与订单金额自此自相矛盾——对外凭据上的错，静默降级最贵。
  const [itemsError, setItemsError] = useState('');
  const [itemsRetry, setItemsRetry] = useState(0);
  const [exporting, setExporting] = useState(false);
  const [exportMsg, setExportMsg] = useState('');
  const previewRef = useRef<HTMLDivElement>(null);
  const scale = 0.6;

  useEffect(() => {
    // 重试点下去会并发两个请求，不认这个标记的话后到的旧响应会盖掉新结果
    let cancelled = false;
    setItemsError('');
    get<OrderItemDetail[]>(`/orders/${order.id}/items`).then(data => {
      if (cancelled) return;
      setOrderItems(data ?? []);
    }).catch(error => {
      if (cancelled) return;
      setItemsError(error instanceof Error && error.message ? error.message : '未知错误');
    });
    return () => { cancelled = true; };
  }, [order.id, itemsRetry]);

  const updateSection = useCallback((key: keyof ExportConfig['sections'], val: boolean) => {
    setConfig(prev => ({ ...prev, sections: { ...prev.sections, [key]: val } }));
  }, []);

  const updateCustomInfo = useCallback((key: keyof ExportConfig['customInfo'], val: string) => {
    setConfig(prev => ({ ...prev, customInfo: { ...prev.customInfo, [key]: val } }));
  }, []);

  const handleExport = async () => {
    if (!previewRef.current) return;
    setExporting(true);
    setExportMsg('正在生成...');
    try {
      const html2canvas = (await import('html2canvas')).default;
      const canvas = await html2canvas(previewRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: config.template === 'business' ? '#1e293b' : config.template === 'warm' ? '#fffbf5' : '#ffffff',
        logging: false,
      });

      const orderNum = order.orderNumber ?? order.id.slice(0, 8).toUpperCase();

      if (config.format === 'png') {
        const link = document.createElement('a');
        link.download = `order-${orderNum}.png`;
        link.href = canvas.toDataURL('image/png');
        link.click();
        setExportMsg('截图已下载！');
      } else {
        const { jsPDF } = await import('jspdf');
        const imgData = canvas.toDataURL('image/png');
        const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
        pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
        pdf.save(`order-${orderNum}.pdf`);
        setExportMsg('PDF 已下载！');
      }

      setTimeout(() => setExportMsg(''), 3000);
    } catch (error) {
      // 吞成一句「导出失败，请重试」会把真因（画布越界、图片跨域、内存不足）全藏起来，
      // 用户重试十次撞的还是同一个错。失败态要能就地指出下一步，先说清是什么。
      const reason = error instanceof Error && error.message ? error.message : '';
      setExportMsg(reason ? `导出失败：${reason}` : '导出失败，请重试');
      setTimeout(() => setExportMsg(''), 6000);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-2xl w-full max-w-5xl mx-4 flex flex-col overflow-hidden" style={{ height: '90vh' }}>
        {/* Modal header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 flex items-center justify-center bg-teal-50 rounded-lg">
              <i className="ri-printer-line text-teal-600 text-sm" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-gray-900">导出订单</h2>
              <p className="text-xs text-gray-400 font-mono">{order.orderNumber ?? order.id.slice(0, 8).toUpperCase()}</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer transition-colors">
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <div className="flex flex-1 overflow-hidden">
          {/* ── Settings panel ── */}
          <div className="w-64 shrink-0 border-r border-gray-100 flex flex-col overflow-y-auto">
            <div className="p-4 space-y-5">

              {/* Template */}
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">版式模板</p>
                <div className="space-y-2">
                  {(Object.keys(TEMPLATE_LABELS) as ExportTemplate[]).map(t => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => setConfig(prev => ({ ...prev, template: t }))}
                      className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg border transition-all cursor-pointer ${config.template === t ? `ring-2 ${TEMPLATE_COLORS[t].ring} border-transparent` : 'border-gray-100 hover:border-gray-200'}`}
                    >
                      <span className={`w-5 h-5 rounded-md ${TEMPLATE_COLORS[t].dot} shrink-0`} />
                      <span className="text-sm text-gray-700 font-medium">{TEMPLATE_LABELS[t]}</span>
                      {config.template === t && (
                        <div className="w-4 h-4 flex items-center justify-center ml-auto">
                          <i className="ri-checkbox-circle-fill text-teal-500 text-sm" />
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Sections */}
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">显示内容</p>
                <div className="divide-y divide-gray-50">
                  <SectionToggle label="客户信息" checked={config.sections.customerInfo} onChange={v => updateSection('customerInfo', v)} />
                  <SectionToggle label="时间安排" checked={config.sections.schedule} onChange={v => updateSection('schedule', v)} />
                  <SectionToggle label="订单明细" checked={config.sections.orderItems} onChange={v => updateSection('orderItems', v)} />
                  <SectionToggle label="付款详情" checked={config.sections.payment} onChange={v => updateSection('payment', v)} />
                  <SectionToggle label="特殊说明" checked={config.sections.specialInstructions} onChange={v => updateSection('specialInstructions', v)} />
                </div>
              </div>

              {/* Watermark */}
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">水印</p>
                <SectionToggle label="显示内部水印" checked={config.showWatermark} onChange={v => setConfig(prev => ({ ...prev, showWatermark: v }))} />
              </div>

              {/* Custom info */}
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">自定义信息</p>
                <div className="space-y-2.5">
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">公司 / 品牌名称</label>
                    <input
                      type="text"
                      placeholder="显示在文档顶部"
                      value={config.customInfo.companyName}
                      onChange={e => updateCustomInfo('companyName', e.target.value)}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-teal-400 text-gray-700 placeholder-gray-300"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">副标题 / 标语</label>
                    <input
                      type="text"
                      placeholder="品牌简介或口号"
                      value={config.customInfo.tagline}
                      onChange={e => updateCustomInfo('tagline', e.target.value)}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-teal-400 text-gray-700 placeholder-gray-300"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">底部备注</label>
                    <input
                      type="text"
                      placeholder="如：感谢您的信任"
                      value={config.customInfo.footerNote}
                      onChange={e => updateCustomInfo('footerNote', e.target.value)}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-teal-400 text-gray-700 placeholder-gray-300"
                    />
                  </div>
                </div>
              </div>

              {/* Format */}
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">导出格式</p>
                <div className="grid grid-cols-2 gap-2">
                  {([['pdf', 'ri-file-pdf-line', 'PDF 文件'], ['png', 'ri-image-line', 'PNG 截图']] as const).map(([fmt, icon, label]) => (
                    <button
                      key={fmt}
                      type="button"
                      onClick={() => setConfig(prev => ({ ...prev, format: fmt }))}
                      className={`flex flex-col items-center gap-1.5 py-3 rounded-lg border transition-all cursor-pointer text-xs font-medium ${config.format === fmt ? 'border-teal-400 bg-teal-50 text-teal-700 ring-1 ring-teal-400' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}
                    >
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className={`${icon} text-base`} />
                      </div>
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Export button */}
            <div className="p-4 mt-auto border-t border-gray-100">
              {itemsError && (
                <div className="text-xs mb-2 py-2 px-2.5 rounded-lg bg-amber-50 text-amber-700 leading-relaxed">
                  订单明细读取失败（{itemsError}），卡片上不会印明细行。
                  <button
                    type="button"
                    onClick={() => setItemsRetry(k => k + 1)}
                    className="ml-1 underline font-medium cursor-pointer"
                  >
                    重试
                  </button>
                </div>
              )}
              {exportMsg && (
                <div className={`text-xs text-center mb-2 py-1.5 rounded-lg ${exportMsg.includes('失败') ? 'bg-rose-50 text-rose-600' : 'bg-teal-50 text-teal-600'}`}>
                  {exportMsg}
                </div>
              )}
              <button
                type="button"
                onClick={handleExport}
                disabled={exporting}
                className="w-full py-2.5 bg-teal-600 hover:bg-teal-700 text-white text-sm font-medium rounded-lg transition-colors cursor-pointer disabled:opacity-60 flex items-center justify-center gap-2 whitespace-nowrap"
              >
                {exporting ? (
                  <>
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-loader-4-line animate-spin text-sm" />
                    </div>
                    生成中...
                  </>
                ) : (
                  <>
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className={`${config.format === 'pdf' ? 'ri-download-line' : 'ri-screenshot-line'} text-sm`} />
                    </div>
                    导出 {config.format.toUpperCase()}
                  </>
                )}
              </button>
            </div>
          </div>

          {/* ── Preview area ── */}
          <div className="flex-1 bg-gray-100 overflow-auto flex items-start justify-center p-8">
            <div
              style={{
                transform: `scale(${scale})`,
                transformOrigin: 'top center',
                width: 794,
                marginBottom: `calc((794px * ${scale} - 794px) * ${scale})`,
              }}
            >
              <div className="rounded-xl overflow-hidden shadow-lg">
                <OrderExportPreview
                  ref={previewRef}
                  order={order}
                  orderItems={orderItems}
                  config={config}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
